import { isEmpty, uniq } from 'lodash/fp';

import { YEAR_KEY } from '@portal/common/models/data-key';
import {
    DataCollectionResource,
    DataGranularity,
    DataType,
    Organization,
} from '@portal/common/types';

import { calculateNowcastYears } from '../forecast-data-helpers';

/**
 * When the currently selected round uses nowcasts (i.e. the round id is
 * present in the dataset's forecast_data_calculation_map) and forecast data
 * calculation is enabled for the organization, extends granularity's year
 * dimension with that round's nowcast years so they become valid filter
 * options. Nowcasts themselves are computed on the frontend and never
 * requested from the backend.
 *
 * Callers must only invoke this when the "Show nowcasts" toggle is active.
 *
 * Important: this function must only consider the selected round's
 * calculation map. Other rounds in the same dataset may use static forecasts
 * (rounds_with_forecast_data + static_forecast_start_times_map) whose years
 * already live in granularity[YEAR_KEY] and must NOT be clipped here, or the
 * data request will exclude those static forecast years.
 */
export const applyNowcastToGranularity = (
    granularity: DataGranularity,
    organization: Organization,
    dataCollection: DataCollectionResource,
    dataTypes: DataType[],
    roundId: number | null | undefined,
    lastPublishedYear: number | null
): DataGranularity => {
    const nowcastYears = getNowcastYearsForRound(
        organization,
        dataCollection,
        dataTypes[0],
        roundId,
        lastPublishedYear
    );
    if (isEmpty(nowcastYears)) {
        return granularity;
    }

    const existingYears = (granularity[YEAR_KEY] as number[] | undefined) ?? [];
    const yearOptions = uniq([...existingYears, ...nowcastYears]).sort((a, b) => a - b);

    return {
        ...granularity,
        [YEAR_KEY]: yearOptions,
    };
};

/**
 * Returns the nowcast year list for the selected round, or [] when the round
 * doesn't use nowcasts (org disabled, round missing from
 * forecast_data_calculation_map, or no calculation config produced).
 */
const getNowcastYearsForRound = (
    organization: Organization,
    dataCollection: DataCollectionResource,
    dataType: DataType,
    roundId: number | null | undefined,
    lastPublishedYear: number | null
): number[] => {
    if (!organization.forecast_data_calculation_is_allowed) {
        return [];
    }
    if (roundId == null || isNaN(roundId)) {
        return [];
    }

    const dataset = dataCollection.datasets.find(({ data_type }) => data_type === dataType);
    const roundCalculationConfig = dataset?.forecast_data_calculation_map?.[String(roundId)];
    if (!roundCalculationConfig) {
        return [];
    }

    return (
        calculateNowcastYears(
            roundCalculationConfig,
            organization.forecast_data_calculation_years,
            lastPublishedYear
        ) ?? []
    );
};

export function appendNowcastYearsToYearOptions(
    yearOptions: number[],
    nowcastYears: number[] | null | undefined
): number[] {
    if (isEmpty(yearOptions) || isEmpty(nowcastYears)) {
        return yearOptions;
    }

    const maxYear = Math.max(...yearOptions);
    const additionalYears = nowcastYears.filter((year) => year > maxYear);
    return [...yearOptions, ...additionalYears];
}
